import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { bundledCatalogImageUrl } from "../catalog-asset-url";
import { captureOAuthCallback } from "../auth/oauth-analytics";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const isSupabaseConfigured = !!(url && key);

let client: SupabaseClient | null = null;
let publicClient: SupabaseClient | null = null;

/** Public document reads never load or refresh a visitor's stored auth session. */
export function getPublicSupabase(): SupabaseClient {
  if (!url || !key) throw new Error("Supabase is not configured");
  publicClient ??= createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
  return publicClient;
}

export function getSupabase(): SupabaseClient {
  if (!client) {
    if (!url || !key) {
      throw new Error(
        "Supabase is not configured — set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local",
      );
    }
    captureOAuthCallback();
    client = createClient(url, key, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    });
  }
  return client;
}

/** Resolve a `ComponentDefinition.image` — a bundled replacement, a bare
 * filename in the catalog-images bucket, or an absolute URL. Unbundled images
 * return null when Supabase isn't configured. */
export function catalogImageUrl(filename: string | undefined): string | null {
  if (!filename) return null;
  const bundledUrl = bundledCatalogImageUrl(filename);
  if (bundledUrl) return bundledUrl;
  if (/^(https?:|data:)/i.test(filename)) return filename;
  if (!url) return null;
  return `${url}/storage/v1/object/public/catalog-images/${filename}`;
}
